import React, { useState, useEffect } from 'react';
import {
  Search,
  Bell,
  Menu,
  ShieldCheck,
  User,
  CheckCircle,
  Building,
  Smartphone,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { UserRole } from '../../types/index.ts';

interface NavbarProps {
  onToggleSidebar: () => void;
  onOpenSearch: () => void;
  onNavigate: (route: string) => void;
  mobileViewMode?: boolean;
  onToggleMobileMode?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  onToggleSidebar,
  onOpenSearch,
  onNavigate,
  mobileViewMode,
  onToggleMobileMode,
}) => {
  const { currentUser, currentRole, setCurrentRole } = useAuth();
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const DEFAULT_NOTIFICATIONS = [
    { id: 1, title: 'Payment Received', message: 'Rs. 4,020,000 received from UBL for Consolidated Invoice C-INV-2026-01.', link: 'consolidated-invoices' },
    { id: 2, title: 'Site Work Milestone', message: 'UBL Clifton Branch tile laying has reached 85% completion.', link: 'projects' },
    { id: 3, title: 'New Maintenance Ticket', message: 'UBL Blue Area branch reported emergency AC water dripping in server room.', link: 'tickets' },
  ];

  const [notifications, setNotifications] = useState<any[]>(DEFAULT_NOTIFICATIONS);

  useEffect(() => {
    fetch('/api/notifications')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setNotifications(data);
        }
      })
      .catch((err) => console.warn('Notifications fetch notice:', err));
  }, []);

  const rolesList: { code: UserRole; label: string; desc: string }[] = [
    { code: 'ADMIN', label: 'Administrator', desc: 'Full system and modules access' },
    { code: 'MANAGEMENT', label: 'Management', desc: 'High-level financial & project oversight' },
    { code: 'PROJECT_MANAGER', label: 'Project Manager', desc: 'Branch projects, BOQ, quotations & site team' },
    { code: 'SITE_ENGINEER', label: 'Site Engineer', desc: 'Mobile site surveys, task progress & completion' },
    { code: 'PURCHASE_OFFICER', label: 'Purchase Officer', desc: 'Material requests, vendors & POs' },
    { code: 'ACCOUNTS', label: 'Accounts', desc: 'Invoices, consolidated billing, payments & expenses' },
  ];

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-30 h-16 flex items-center justify-between px-4 sm:px-6 shadow-2xs">
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 lg:hidden focus:outline-none"
          title="Toggle Navigation"
        >
          <Menu className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => onNavigate('dashboard')}>
          <div className="w-9 h-9 rounded-lg bg-[#0f2942] text-amber-400 flex items-center justify-center font-bold text-lg shadow-xs">
            NB
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-slate-900 tracking-tight text-base sm:text-lg leading-tight">
                NAEEM BUILDER
              </span>
              <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                UBL ERP
              </span>
            </div>
            <p className="text-[11px] text-slate-500 hidden sm:block">
              Renovation, Maintenance & Project Engineering
            </p>
          </div>
        </div>
      </div>

      {/* Global Search Bar */}
      <div className="hidden md:flex flex-1 max-w-md mx-6">
        <button
          onClick={onOpenSearch}
          className="w-full flex items-center justify-between px-3.5 py-2 text-sm text-slate-400 bg-slate-100/80 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500">Search Tickets, Projects, POs, Invoices, Branches...</span>
          </div>
          <kbd className="text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-500 font-mono shadow-2xs">
            Ctrl+K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Mobile View Toggle button */}
        {onToggleMobileMode && (
          <button
            onClick={onToggleMobileMode}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              mobileViewMode
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
            }`}
            title="Toggle Field Engineer Mobile Mode"
          >
            <Smartphone className="w-4 h-4" />
            <span className="hidden sm:inline">Site Mobile Mode</span>
          </button>
        )}

        {/* Global Search icon for mobile */}
        <button
          onClick={onOpenSearch}
          className="md:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
          title="Search"
        >
          <Search className="w-5 h-5" />
        </button>

        {/* Role Switcher */}
        <div className="relative">
          <button
            onClick={() => setShowRoleDropdown(!showRoleDropdown)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200/80 text-slate-800 border border-slate-200 transition-colors"
          >
            <ShieldCheck className="w-4 h-4 text-blue-700" />
            <span className="hidden sm:inline">Role:</span>
            <span className="text-blue-900 font-bold">{currentRole.replace('_', ' ')}</span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>

          {showRoleDropdown && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50">
              <div className="px-3 py-1.5 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Test Role Perspectives
                </p>
                <p className="text-[11px] text-slate-400">
                  Switch roles to verify UI permissions & menus
                </p>
              </div>
              <div className="py-1">
                {rolesList.map((r) => (
                  <button
                    key={r.code}
                    onClick={() => {
                      setCurrentRole(r.code);
                      setShowRoleDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs flex items-start gap-2.5 hover:bg-blue-50 transition-colors ${
                      currentRole === r.code ? 'bg-blue-50/70 font-semibold text-blue-900' : 'text-slate-700'
                    }`}
                  >
                    <div className="mt-0.5">
                      {currentRole === r.code ? (
                        <CheckCircle className="w-3.5 h-3.5 text-blue-600" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border border-slate-300" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">{r.label}</div>
                      <div className="text-[10px] text-slate-500 font-normal leading-tight">{r.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-lg focus:outline-none"
            title="Notifications"
          >
            <Bell className="w-5 h-5" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          </button>

          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
              <div className="flex items-center justify-between px-3.5 py-1.5 border-b border-slate-100">
                <span className="text-xs font-bold text-slate-800">Notifications & Alerts</span>
                <span className="text-[10px] text-blue-600 cursor-pointer font-medium hover:underline">
                  Mark all read
                </span>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                {!Array.isArray(notifications) || notifications.length === 0 ? (
                  <div className="p-4 text-center text-xs text-slate-400">No new notifications</div>
                ) : (
                  (Array.isArray(notifications) ? notifications : []).map((n) => (
                    <div
                      key={n.id}
                      onClick={() => {
                        if (n.link) onNavigate(n.link.replace('/', ''));
                        setShowNotifications(false);
                      }}
                      className="p-3 hover:bg-slate-50 cursor-pointer text-xs"
                    >
                      <div className="font-semibold text-slate-800">{n.title}</div>
                      <p className="text-slate-600 text-[11px] mt-0.5">{n.message}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Profile */}
        <div className="flex items-center gap-2 pl-2 border-l border-slate-200">
          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center font-bold text-xs">
            {currentUser.name.charAt(0)}
          </div>
          <div className="hidden xl:block text-left">
            <div className="text-xs font-semibold text-slate-800 truncate max-w-[120px]">
              {currentUser.name}
            </div>
            <div className="text-[10px] text-slate-500 truncate max-w-[120px]">
              {currentUser.designation || currentUser.role}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
